import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Select, Switch, Textarea, useToast } from '../../../ui/components';
import { useT } from '../../../i18n';
import { suppliersApi, SUPPLIER_STATUSES } from '../suppliersApi';
import { settingsApi, INCOTERMS } from '../../settings/settingsApi';
import { toUpdatePayload } from './utils';

const RATING_OPTIONS = [
  { value: '', label: 'Not rated' },
  { value: '1', label: '1 - Poor' },
  { value: '2', label: '2 - Fair' },
  { value: '3', label: '3 - Good' },
  { value: '4', label: '4 - Very good' },
  { value: '5', label: '5 - Excellent' },
];

const NONE = '';

/** Ensures a currently-stored value still appears as an option even if it's no longer configured. */
function withCurrent(options, current, label) {
  if (!current || options.some((o) => o.value === current)) return options;
  return [...options, { value: current, label: label ? label(current) : `${current} (custom)` }];
}

function initForm(s) {
  return {
    name: s.name ?? '',
    code: s.code ?? '',
    legalName: s.legalName ?? '',
    tradeName: s.tradeName ?? '',
    status: s.status ?? 'Draft',
    category: s.category ?? '',
    isPreferred: !!s.isPreferred,
    rating: s.rating ? String(s.rating) : '',
    email: s.email ?? '',
    phone: s.phone ?? '',
    website: s.website ?? '',
    currency: s.currency ?? 'USD',
    paymentTermName: s.paymentTermName ?? '',
    paymentTermsDays: s.paymentTermsDays ?? '',
    defaultLeadTimeDays: s.defaultLeadTimeDays ?? '',
    incoterms: s.incoterms ?? '',
    preferredPaymentMethod: s.preferredPaymentMethod ?? '',
    minimumOrderAmount: s.minimumOrderValue?.amount ?? '',
    taxId: s.taxId ?? '',
    registrationNumber: s.registrationNumber ?? '',
    prostayAccountingSupplierId: s.prostayAccountingSupplierId ?? '',
    tableViewSupplierId: s.tableViewSupplierId ?? '',
    tags: (s.tags || []).join(', '),
    description: s.description ?? '',
  };
}

export default function ProfileTab({ supplier, onSaved }) {
  const toast = useToast();
  const t = useT();
  const [form, setForm] = useState(() => initForm(supplier));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const eff = await settingsApi.getEffective(supplier.propertyId || null);
        if (active) setSettings(eff);
      } catch {
        /* fall back to whatever is stored on the supplier */
      }
    })();
    return () => { active = false; };
  }, [supplier.propertyId]);

  const statusOptions = useMemo(() => SUPPLIER_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })), [t]);

  const customLabel = useCallback((c) => t('{value} (custom)', { value: c }), [t]);

  const currencyOptions = useMemo(
    () => withCurrent((settings?.currencies || []).map((c) => ({ value: c, label: c })), form.currency, customLabel),
    [settings, form.currency, customLabel],
  );

  const termOptions = useMemo(() => {
    const base = [{ value: NONE, label: t('No term') }];
    const opts = (settings?.paymentTerms || []).map((term) => ({
      value: term.name,
      label: term.days != null ? t('{name} · {days} days', { name: term.name, days: term.days }) : term.name,
    }));
    return withCurrent([...base, ...opts], form.paymentTermName, customLabel);
  }, [settings, form.paymentTermName, t, customLabel]);

  const methodOptions = useMemo(() => {
    const base = [{ value: NONE, label: t('No method') }];
    const opts = (settings?.paymentMethods || []).map((m) => ({ value: m.name, label: m.name }));
    return withCurrent([...base, ...opts], form.preferredPaymentMethod, customLabel);
  }, [settings, form.preferredPaymentMethod, t, customLabel]);

  const incotermOptions = useMemo(
    () => withCurrent([{ value: NONE, label: t('No terms') }, ...INCOTERMS], form.incoterms, customLabel),
    [form.incoterms, t, customLabel],
  );

  const ratingOptions = useMemo(
    () => RATING_OPTIONS.map((o) => ({ ...o, label: t(o.label) })),
    [t],
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setVal = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const onTermChange = (name) => {
    const term = (settings?.paymentTerms || []).find((pt) => pt.name === name);
    setForm((f) => ({
      ...f,
      paymentTermName: name,
      paymentTermsDays: term ? term.days : name === NONE ? '' : f.paymentTermsDays,
    }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('Name is required.');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = t('Enter a valid email.');
    if (!form.currency) next.currency = t('Select a currency.');
    if (form.defaultLeadTimeDays !== '' && (Number(form.defaultLeadTimeDays) < 0 || Number(form.defaultLeadTimeDays) > 365))
      next.defaultLeadTimeDays = '0–365.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);

    const currency = (form.currency || 'USD').toUpperCase();
    const amount = form.minimumOrderAmount === '' ? null : Number(form.minimumOrderAmount);

    const payload = toUpdatePayload(supplier, {
      name: form.name.trim(),
      code: form.code.trim() || null,
      legalName: form.legalName.trim() || null,
      tradeName: form.tradeName.trim() || null,
      status: form.status,
      category: form.category.trim() || null,
      isPreferred: form.isPreferred,
      rating: form.rating === '' ? null : Number(form.rating),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      website: form.website.trim() || null,
      currency,
      paymentTermName: form.paymentTermName || null,
      paymentTermsDays: form.paymentTermsDays === '' ? null : Number(form.paymentTermsDays),
      defaultLeadTimeDays: form.defaultLeadTimeDays === '' ? null : Number(form.defaultLeadTimeDays),
      incoterms: form.incoterms || null,
      preferredPaymentMethod: form.preferredPaymentMethod || null,
      minimumOrderValue: amount == null ? null : { amount, currency },
      taxId: form.taxId.trim() || null,
      registrationNumber: form.registrationNumber.trim() || null,
      prostayAccountingSupplierId: form.prostayAccountingSupplierId.trim() || null,
      tableViewSupplierId: form.tableViewSupplierId.trim() || null,
      tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      description: form.description.trim() || null,
    });

    try {
      const res = await suppliersApi.update(supplier.id, payload);
      onSaved(res);
      toast.success(t('Profile saved'));
    } catch (err) {
      toast.error(t('Could not save profile'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="sp-panel" onSubmit={handleSubmit}>
      <Card>
        <Card.Header><Card.Title>{t('Identity')}</Card.Title></Card.Header>
        <Card.Body>
          <div className="sp-form">
            <div className="sp-form__row">
              <Input label={t('Display name')} value={form.name} onChange={set('name')} error={errors.name} />
              <Input label={t('Supplier code')} value={form.code} onChange={set('code')} placeholder="ACME-001" />
            </div>
            <div className="sp-form__row">
              <Input label={t('Legal name')} value={form.legalName} onChange={set('legalName')} />
              <Input label={t('Trade name (DBA)')} value={form.tradeName} onChange={set('tradeName')} />
            </div>
            <div className="sp-form__row--3">
              <div className="ps-field">
                <span className="sp-field-label">{t('Status')}</span>
                <Select value={form.status} onChange={setVal('status')} options={statusOptions} ariaLabel={t('Status')} />
              </div>
              <Input label={t('Category')} value={form.category} onChange={set('category')} placeholder={t('Food & Beverage')} />
              <div className="ps-field">
                <span className="sp-field-label">{t('Rating')}</span>
                <Select value={form.rating} onChange={setVal('rating')} options={ratingOptions} ariaLabel={t('Rating')} />
              </div>
            </div>
            <Switch
              checked={form.isPreferred}
              onChange={(v) => setForm((f) => ({ ...f, isPreferred: v }))}
              label={t('Preferred supplier')}
              hint={t('Highlight this supplier in selection lists.')}
            />
            <Input label={t('Tags')} value={form.tags} onChange={set('tags')} hint={t('Comma-separated, e.g. organic, local, certified')} />
            <Textarea label={t('About / description')} value={form.description} onChange={set('description')} rows={3} autoGrow maxRows={8} />
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><Card.Title>{t('Contact channels')}</Card.Title></Card.Header>
        <Card.Body>
          <div className="sp-form">
            <div className="sp-form__row--3">
              <Input label={t('Email')} type="email" value={form.email} onChange={set('email')} error={errors.email} />
              <Input label={t('Phone')} value={form.phone} onChange={set('phone')} />
              <Input label={t('Website')} value={form.website} onChange={set('website')} placeholder="https://" />
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>{t('Commercial terms')}</Card.Title>
          <Card.Subtitle>{t('Options are configured in Settings → Commercial standards.')}</Card.Subtitle>
        </Card.Header>
        <Card.Body>
          <div className="sp-form">
            <div className="sp-form__row--3">
              <div className="ps-field">
                <span className="sp-field-label">{t('Currency')}</span>
                <Select value={form.currency} onChange={setVal('currency')} options={currencyOptions} ariaLabel={t('Currency')} />
                {errors.currency && <span className="ps-field__error">{errors.currency}</span>}
              </div>
              <div className="ps-field">
                <span className="sp-field-label">{t('Payment terms')}</span>
                <Select value={form.paymentTermName} onChange={onTermChange} options={termOptions} placeholder={t('No term')} ariaLabel={t('Payment terms')} />
              </div>
              <Input label={t('Lead time (days)')} type="number" value={form.defaultLeadTimeDays} onChange={set('defaultLeadTimeDays')} error={errors.defaultLeadTimeDays} />
            </div>
            <div className="sp-form__row--3">
              <div className="ps-field">
                <span className="sp-field-label">{t('Incoterms')}</span>
                <Select value={form.incoterms} onChange={setVal('incoterms')} options={incotermOptions} placeholder={t('No terms')} ariaLabel={t('Incoterms')} />
              </div>
              <div className="ps-field">
                <span className="sp-field-label">{t('Payment method')}</span>
                <Select value={form.preferredPaymentMethod} onChange={setVal('preferredPaymentMethod')} options={methodOptions} placeholder={t('No method')} ariaLabel={t('Payment method')} />
              </div>
              <Input label={t('Min. order value')} type="number" value={form.minimumOrderAmount} onChange={set('minimumOrderAmount')} />
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><Card.Title>{t('Compliance & ecosystem links')}</Card.Title></Card.Header>
        <Card.Body>
          <div className="sp-form">
            <div className="sp-form__row">
              <Input label={t('Tax ID')} value={form.taxId} onChange={set('taxId')} placeholder="VAT / EIN" />
              <Input label={t('Registration number')} value={form.registrationNumber} onChange={set('registrationNumber')} />
            </div>
            <div className="sp-form__row">
              <Input label={t('Prostay Accounting supplier ID')} value={form.prostayAccountingSupplierId} onChange={set('prostayAccountingSupplierId')} />
              <Input label={t('TableView supplier ID')} value={form.tableViewSupplierId} onChange={set('tableViewSupplierId')} />
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="sp-form__actions">
        <Button type="submit" variant="primary" loading={saving}>{t('Save changes')}</Button>
      </div>
    </form>
  );
}
